import { useEffect, useState } from 'react';
import client from '../api/client';

export interface WatermarkConfig {
  enabled: boolean;
  logoUrl: string;
  text: string;
  opacity: number;
}

const FALLBACK_WATERMARK: WatermarkConfig = { enabled: true, logoUrl: '/favicon.ico', text: 'DTwince 云边智孪', opacity: 0.18 };

/** 是否登录态：匿名查看器（/s/*、?scene= 免登录）不做 /auth/* 探测，用 meta 显式水印开关 */
function hasAuthToken(): boolean {
  try {
    return !!localStorage.getItem('dtwin_token') || !!localStorage.getItem('token');
  } catch {
    return false;
  }
}

/** 水印配置（服务端下发，防前端篡改）——模块级缓存，一次拉取 */
let cfgPromise: Promise<WatermarkConfig> | null = null;

export function getWatermarkConfig(): Promise<WatermarkConfig> {
  if (!hasAuthToken()) return Promise.resolve(FALLBACK_WATERMARK);
  if (!cfgPromise) {
    cfgPromise = client
      .get('/auth/watermark/config')
      .then((r) => r.data)
      .catch(() => FALLBACK_WATERMARK);
  }
  return cfgPromise;
}

/** D-06：当前用户是否带水印（免费/基础带，专业/企业去；读取失败按安全策略=带水印）
 *  2026-10-03 修复：返回 null = 配额加载中（3D 水印/顶部条此时不渲染，避免付费用户进入编辑器时先闪现水印再消失） */
export function useWatermarkFlag(): boolean | null {
  const [flag, setFlag] = useState<boolean | null>(null);
  useEffect(() => {
    if (!hasAuthToken()) return;
    let cancelled = false;
    client
      .get('/auth/quota')
      .then((r) => {
        if (!cancelled) setFlag(r.data?.watermark !== false);
      })
      .catch(() => {
        if (!cancelled) setFlag(true);
      });
    return () => { cancelled = true; };
  }, []);
  return flag;
}

/** 水印叠加层：公司 logo + 文字（左下角，半透明，指针穿透） */
export default function Watermark({ show, style, testId = 'dashboard-watermark' }: { show: boolean; style?: React.CSSProperties; testId?: string }) {
  const [cfg, setCfg] = useState<WatermarkConfig | null>(null);
  useEffect(() => {
    getWatermarkConfig().then(setCfg);
  }, []);
  if (!show || !cfg?.enabled) return null;
  return (
    <div data-testid={testId} style={{ position: 'absolute', inset: 0, zIndex: 9999, pointerEvents: 'none', overflow: 'hidden', ...style }}>
      <div style={{ position: 'absolute', left: 16, bottom: 12, display: 'flex', alignItems: 'center', gap: 8, opacity: cfg.opacity, userSelect: 'none' }}>
        <img src={cfg.logoUrl} alt="" style={{ height: 22, borderRadius: 4, opacity: 0.9 }} />
        <span style={{ color: '#fff', fontSize: 13, letterSpacing: 1 }}>{cfg.text}</span>
      </div>
    </div>
  );
}